'use client';

import { type ChangeEvent, useContext } from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { LoadingBall } from '@depop/web-ui-kit/LoadingBall';
import { usePathname, useSearchParams } from 'next/navigation';
import { v4 as uuidv4 } from 'uuid';

import { Table } from '@/components/AdminToolbox/components/Table';
import { RefererContext } from '@/modules/activityTracker/RefererProvider';
import { getCurrencySymbolFromCurrencyCode } from '@/modules/currencies/helpers';
import { type CurrencyCode } from '@/modules/location/constants';
import { getProductPriceFromPricing } from '@/modules/price/helpers/getProductPriceFromPricing';
import { isExternalPLALander } from '@/modules/product/helpers/isExternalPLALander';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';
import { useProductBySlugExtendedPDP } from '@/modules/product/hooks/useProductBySlugExtendedPDP';
import { useExtendedRouter } from '@/modules/routing/useExtendedRouter';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function ProductInfo() {
  const { data: productBasic, isPending: productBasicPending } =
    useProductBySlugBasicPDP();
  const { data: productExtended, isPending: productExtendedPending } =
    useProductBySlugExtendedPDP();
  const referer = useContext(RefererContext);
  const params = useSearchParams();
  const isPLA = isExternalPLALander({ referer, params });
  const router = useExtendedRouter();
  const path = usePathname();
  const [, currentUser] = useCurrentUser();
  const userCountry = currentUser?.country;

  if (productBasicPending || productExtendedPending) {
    return <LoadingBall />;
  }

  if (!productBasic || !productExtended) {
    return <Body variant="sm-regular">Failed to fetch product data</Body>;
  }

  function togglePLA(e: ChangeEvent<HTMLInputElement>) {
    const newParams = new URLSearchParams(params.toString());
    if (e.target.checked) {
      newParams.set('gclid', uuidv4());
    } else {
      newParams.delete('gclid');
    }
    const newQuery = newParams.toString();
    router.replace(newQuery ? `${path}?${newQuery}` : path);
  }

  const { national_shipping_cost, international_shipping_cost, currency } =
    productExtended.pricing;
  const price = getProductPriceFromPricing(productExtended.pricing);
  const currencySymbol = getCurrencySymbolFromCurrencyCode(
    currency as CurrencyCode
  );
  const nationalShippingCost = national_shipping_cost?.total_price
    ? `${currencySymbol}${national_shipping_cost.total_price}`
    : 'N/A';
  const internationalShippingCost = international_shipping_cost?.total_price
    ? `${currencySymbol}${international_shipping_cost.total_price}`
    : 'N/A';

  return (
    <div>
      <Table
        rows={{
          'Product Id': productBasic.id,
          'Product Country': productBasic.country,
          'Product Slug': productBasic.slug,
          'Product Group': productBasic.attributes.group,
          'Product Type': productBasic.attributes.product_type,
          'Product Brand Id': productBasic?.brand_id || 'N/A',
          'National Shipping Cost': nationalShippingCost,
          'International Shipping Cost': internationalShippingCost,
          'Eligible For Marketplace Fees':
            Boolean(price.currentPriceFees) &&
            Number(price.currentPriceFees) > 0,
          'Eligible For Sales Tax':
            Boolean(price.currentSalesTax) && Number(price.currentSalesTax) > 0,
          'Seller Id': productBasic.seller.id,
          'Seller Username': productBasic.seller.username,
          'Seller Country': productBasic.seller.country,
          'Buyer Country': userCountry || '- Login for result -',
          'Toggle PLA': () => (
            <input
              type="checkbox"
              checked={isPLA}
              onChange={togglePLA}
              aria-label="Toggle PLA mode"
            />
          ),
        }}
      />
    </div>
  );
}
